import React, { useState, useEffect, useReducer } from 'react';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import API_BASE_URL from '../config/api';
import { getExchangeController } from '../config/exchangeControllers';
import { CANONICAL_NETWORKS, TRANSFER_FEE_EXCHANGES } from '../config/transferNetworks';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatAmount = (num) => {
  if (num === null || num === undefined) return '0';
  const n = parseFloat(num);
  if (n === 0) return '0';
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (Math.abs(n) >= 1)    return n.toFixed(4);
  return n.toFixed(6);
};

// Collect all currencies across all balance data, USDT always first then alphabetical
const buildCurrencyColumns = (balanceMap, accountTypes) => {
  const set = new Set(['USDT']); // always show at least USDT
  Object.values(balanceMap).forEach(entries => {
    if (!Array.isArray(entries)) return;
    entries.forEach(b => {
      if (accountTypes.includes(b.accountType)) set.add(b.symbol.toUpperCase());
    });
  });
  const rest = [...set].filter(s => s !== 'USDT').sort();
  return ['USDT', ...rest];
};

// Get balance entry for an exchange+symbol+accountTypes
const getBalanceEntry = (balanceMap, exchangeId, symbol, accountTypes) => {
  const entries = balanceMap[exchangeId];
  if (!Array.isArray(entries)) return null;
  return entries.find(
    b => b.symbol.toUpperCase() === symbol && accountTypes.includes(b.accountType)
  ) || null;
};

// ---------------------------------------------------------------------------
// Summary table component
// ---------------------------------------------------------------------------

const CombinedSummaryTable = ({ futuresSettings, spotSettings, allExchangeInfo, exchangesWithKeys, balances, loading, errors, notSupported }) => {
  const futuresTypes = ['futures', 'all'];
  const spotTypes    = ['spot', 'main', 'all'];

  const currencies = buildCurrencyColumns(balances, [...futuresTypes, ...spotTypes]);

  const futuresIds = new Set(futuresSettings.map(s => s.exchange_id));
  const spotIds    = new Set(spotSettings.map(s => s.exchange_id));
  const allIds     = [...new Set([...futuresSettings, ...spotSettings].map(s => s.exchange_id))];

  if (allIds.length === 0) return null;

  const monoFont = "'Courier New', Courier, monospace";

  const cellStyle = {
    fontSize: '12px',
    fontFamily: monoFont,
    padding: '3px 6px',
    textAlign: 'right',
    minWidth: '54px',
    verticalAlign: 'top',
    borderBottom: '1px solid #21262d',
  };

  const headerCellStyle = {
    fontSize: '12px',
    fontFamily: monoFont,
    fontWeight: '500',
    color: '#8b949e',
    padding: '3px 6px',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #2a2d3a',
  };

  const renderCell = (exId, cur, accountTypes, enabled, isPerp) => {
    const perpBorder = isPerp ? { borderLeft: '2px solid #4a5060' } : {};
    if (!enabled) {
      return <td style={{ ...cellStyle, ...perpBorder, color: '#3d4450' }}><div>—</div><div>—</div></td>;
    }
    const hasKey    = exchangesWithKeys.has(exId);
    const isLoading = loading[exId];
    const hasError  = errors[exId];

    if (!hasKey)   return <td style={{ ...cellStyle, ...perpBorder, color: '#8b949e' }}><div>N/A</div><div>N/A</div></td>;
    if (isLoading) return <td style={{ ...cellStyle, ...perpBorder, color: '#8b949e' }}><div>...</div><div>...</div></td>;
    if (hasError)  return <td style={{ ...cellStyle, ...perpBorder, color: '#f85149' }}><div>Err</div><div>Err</div></td>;

    const exNotSupported = notSupported?.[exId] || [];
    const typeLabel = isPerp ? 'futures' : 'spot';
    if (exNotSupported.includes(typeLabel)) {
      return <td style={{ ...cellStyle, ...perpBorder, color: '#8b949e' }}><div>NotSup</div><div>—</div></td>;
    }

    const entry = getBalanceEntry(balances, exId, cur, accountTypes);
    if (!entry) return <td style={{ ...cellStyle, ...perpBorder, color: '#8b949e' }}><div>0</div><div>0</div></td>;

    return (
      <td style={{ ...cellStyle, ...perpBorder }}>
        <div style={{ color: '#ffffff' }}>{formatAmount(parseFloat(entry.balance))}</div>
        <div style={{ color: '#3fb950' }}>{formatAmount(parseFloat(entry.available))}</div>
      </td>
    );
  };

  return (
    <div className="data-table" style={{ padding: '12px', marginBottom: '0', overflowX: 'auto', display: 'inline-block', width: 'auto' }}>
      <h3 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '600', color: '#ffffff', fontFamily: "'Courier New', Courier, monospace" }}>
        Balance Overview
      </h3>
      <table style={{ fontSize: '12px', fontFamily: "'Courier New', Courier, monospace", borderCollapse: 'collapse', width: 'auto', border: '2px solid #4a5060' }}>
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...headerCellStyle, textAlign: 'left', minWidth: '110px', verticalAlign: 'bottom' }}>Exchange</th>
            {currencies.map(cur => (
              <th key={cur} colSpan={2} style={{ ...headerCellStyle, textAlign: 'center', borderLeft: '2px solid #4a5060' }}>{cur}</th>
            ))}
          </tr>
          <tr>
            {currencies.map(cur => (
              <React.Fragment key={cur}>
                <th style={{ ...headerCellStyle, borderLeft: '2px solid #4a5060', fontSize: '10px' }}>Perp</th>
                <th style={{ ...headerCellStyle, fontSize: '10px' }}>Spot</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {allIds.map(exId => {
            const exInfo = allExchangeInfo[exId] || {};
            return (
              <tr key={exId}>
                <td style={{ ...cellStyle, textAlign: 'left', color: '#ffffff', fontWeight: '500', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {exInfo.exchange_icon_link && exInfo.exchange_icon_link.startsWith('http') ? (
                      <img src={exInfo.exchange_icon_link} alt={exInfo.exchange_name} style={{ width: '13px', height: '13px', objectFit: 'contain' }} />
                    ) : null}
                    {exInfo.exchange_name || `Exchange ${exId}`}
                  </div>
                </td>
                {currencies.map(cur => (
                  <React.Fragment key={cur}>
                    {renderCell(exId, cur, futuresTypes, futuresIds.has(exId), true)}
                    {renderCell(exId, cur, spotTypes,    spotIds.has(exId),    false)}
                  </React.Fragment>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: '6px', fontSize: '11px', fontFamily: "'Courier New', Courier, monospace", color: '#8b949e' }}>
        Perp = Perpetual Futures &nbsp;·&nbsp; Spot = Spot &nbsp;·&nbsp; Top line: total &nbsp;·&nbsp; <br/> Bottom line: available &nbsp;·&nbsp; N/A: no API key &nbsp;·&nbsp; — : not enabled &nbsp;·&nbsp; <br/>NotSup: not supported or limited by exchange
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Transfer Fees table component
// ---------------------------------------------------------------------------

const TransferFeesTable = ({ userId, exchangesWithKeys }) => {
  const [feeData, setFeeData]       = useState({});   // { exchangeKey: [ {network, networkName, withdrawFee, ...} ] }
  const [feeLoading, setFeeLoading] = useState({});
  const [feeErrors, setFeeErrors]   = useState({});

  useEffect(() => {
    if (!userId) return;
    TRANSFER_FEE_EXCHANGES.forEach(ex => {
      if (!exchangesWithKeys.has(ex.exchangeId)) return;
      const url = `${API_BASE_URL}${ex.endpoint.replace(':userId', userId)}`;
      setFeeLoading(prev => ({ ...prev, [ex.exchangeKey]: true }));
      fetch(url)
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            setFeeData(prev => ({ ...prev, [ex.exchangeKey]: data.data }));
          } else {
            setFeeErrors(prev => ({ ...prev, [ex.exchangeKey]: data.error || 'Error' }));
          }
        })
        .catch(err => setFeeErrors(prev => ({ ...prev, [ex.exchangeKey]: err.message })))
        .finally(() => setFeeLoading(prev => ({ ...prev, [ex.exchangeKey]: false })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, exchangesWithKeys]);

  const eqNet = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

  // Build column list: canonical mandatory first, then extras from fetched data
  const buildColumns = () => {
    const extraCols = [];

    TRANSFER_FEE_EXCHANGES.forEach(ex => {
      const networks = feeData[ex.exchangeKey] || [];
      networks.forEach(net => {
        // Check if this network maps to a canonical column (case-insensitive)
        const canonical = CANONICAL_NETWORKS.find(c =>
          eqNet(c.exchangeCodes[ex.exchangeKey], net.network)
        );
        if (!canonical) {
          const existsAlready = extraCols.find(e => eqNet(e.id, net.network));
          if (!existsAlready) {
            extraCols.push({ id: net.network, label: net.network.toUpperCase(), fullName: net.networkName, isExtra: true });
          }
        }
      });
    });

    return [...CANONICAL_NETWORKS.map(n => ({ ...n, isExtra: false })), ...extraCols];
  };

  const getCell = (exchangeKey, col) => {
    if (feeLoading[exchangeKey]) return { state: 'loading' };
    if (feeErrors[exchangeKey])  return { state: 'error', msg: feeErrors[exchangeKey] };
    const networks = feeData[exchangeKey];
    if (!networks) return { state: 'na' };

    // Find the exchange-specific network code for this column (case-insensitive)
    let networkCode = col.exchangeCodes?.[exchangeKey];
    if (!networkCode && col.isExtra) networkCode = col.id;
    if (!networkCode) return { state: 'na' };

    const net = networks.find(n => eqNet(n.network, networkCode));
    if (!net) return { state: 'na' };
    return { state: 'ok', net };
  };

  const configuredExchanges = TRANSFER_FEE_EXCHANGES.filter(ex => exchangesWithKeys.has(ex.exchangeId));
  if (configuredExchanges.length === 0) return null;

  const columns = buildColumns();
  const monoFont = "'Courier New', Courier, monospace";

  const headerCellStyle = {
    fontSize: '11px',
    fontFamily: monoFont,
    fontWeight: '500',
    color: '#8b949e',
    padding: '3px 6px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #2a2d3a',
    borderLeft: '1px solid #2a2d3a',
  };

  const cellStyle = {
    fontSize: '11px',
    fontFamily: monoFont,
    padding: '3px 6px',
    textAlign: 'center',
    verticalAlign: 'top',
    borderBottom: '1px solid #21262d',
    borderLeft: '1px solid #2a2d3a',
  };

  return (
    <div className="data-table" style={{ padding: '12px', marginTop: '16px', overflowX: 'auto', display: 'inline-block', width: 'auto' }}>
      <h3 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '600', color: '#ffffff', fontFamily: monoFont }}>
        Transfer Fees (USDT) - Under development
      </h3>
      <table style={{ fontSize: '11px', fontFamily: monoFont, borderCollapse: 'collapse', width: 'auto', border: '2px solid #4a5060' }}>
        <thead>
          <tr>
            <th style={{ ...headerCellStyle, textAlign: 'left', borderLeft: 'none', minWidth: '120px' }}>Network</th>
            {configuredExchanges.map(ex => (
              <th key={ex.exchangeKey} style={headerCellStyle}>{ex.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {columns.map(col => (
            <tr key={col.id}>
              <td style={{ ...cellStyle, textAlign: 'left', borderLeft: 'none', color: '#ffffff', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                <div style={{ fontWeight: '500' }}>{col.label}</div>
                <div style={{ fontSize: '9px', color: '#6a737d' }}>{col.fullName}</div>
              </td>
              {configuredExchanges.map(ex => {
                const cell = getCell(ex.exchangeKey, col);
                if (cell.state === 'loading') {
                  return <td key={ex.exchangeKey} style={{ ...cellStyle, color: '#8b949e' }}>…</td>;
                }
                if (cell.state === 'error') {
                  return <td key={ex.exchangeKey} style={{ ...cellStyle, color: '#f85149' }}>Err</td>;
                }
                if (cell.state === 'na') {
                  return <td key={ex.exchangeKey} style={{ ...cellStyle, color: '#3d4450' }}>N/A</td>;
                }
                const { net } = cell;
                return (
                  <td key={ex.exchangeKey} style={{ ...cellStyle, color: net.withdrawEnable ? '#ffffff' : '#8b949e' }}>
                    <div>{net.feeNotes ?? net.withdrawFee}</div>
                    {!net.withdrawEnable && <div style={{ fontSize: '9px', color: '#f85149' }}>disabled</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: '6px', fontSize: '11px', fontFamily: monoFont, color: '#8b949e' }}>
        Fee shown is USDT withdrawal fee &nbsp;·&nbsp; N/A: network not supported
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// Reducer for per-exchange balance state — one dispatch = one re-render
const balanceReducer = (state, action) => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, loading: { ...state.loading, [action.id]: true }, errors: { ...state.errors, [action.id]: null } };
    case 'SUCCESS':
      return {
        ...state,
        loading:      { ...state.loading,      [action.id]: false },
        balances:     { ...state.balances,     [action.id]: action.data },
        errors:       { ...state.errors,       [action.id]: null },
        notSupported: { ...state.notSupported, [action.id]: action.notSupported || [] }
      };
    case 'ERROR':
      return { ...state, loading: { ...state.loading, [action.id]: false }, errors: { ...state.errors, [action.id]: action.error } };
    case 'RESET':
      return { balances: {}, loading: {}, errors: {}, notSupported: {} };
    default:
      return state;
  }
};

const Balances = () => {
  const { user } = useAuth();
  const [exchanges, setExchanges] = useState([]);
  const [{ balances, loading, errors, notSupported }, dispatchBalance] = useReducer(balanceReducer, { balances: {}, loading: {}, errors: {}, notSupported: {} });

  // Summary table state
  const [userExchangeSettings, setUserExchangeSettings] = useState([]);
  const [allExchangeInfo, setAllExchangeInfo] = useState({});
  const [exchangesWithKeys, setExchangesWithKeys] = useState(new Set());

  // Depend on user.sub (stable string) not the user object reference
  useEffect(() => {
    if (!user?.sub) return;
    dispatchBalance({ type: 'RESET' });
    fetchAllExchangeInfo();
    fetchUserExchangeSettings();
    fetchExchangesWithKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub]);

  const fetchAllExchangeInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/exchanges`);
      const data = await response.json();
      if (data.success) {
        const map = {};
        data.data.forEach(ex => { map[ex.id] = ex; });
        setAllExchangeInfo(map);
      }
    } catch (error) {
      console.error('Error fetching all exchanges:', error);
    }
  };

  const fetchUserExchangeSettings = async () => {
    if (!user?.id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/exchanges/user-settings/${user.id}`);
      const data = await response.json();
      if (data.success) {
        setUserExchangeSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching user exchange settings:', error);
    }
  };

  const fetchExchangesWithKeys = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/api-keys/${user.sub}/configured`);
      const data = await response.json();
      if (data.success) {
        setExchanges(data.data);
        const keySet = new Set(data.data.map(ex => ex.exchangeId));
        setExchangesWithKeys(keySet);
        // Fetch balances for each exchange with a key
        data.data.forEach(exchange => {
          fetchExchangeBalances(exchange);
        });
      }
    } catch (error) {
      console.error('Error fetching exchanges:', error);
    }
  };

  const fetchExchangeBalances = async (exchange) => {
    const exchangeId = exchange.exchangeId;
    const controller = getExchangeController(exchangeId);

    if (!controller) {
      dispatchBalance({ type: 'ERROR', id: exchangeId, error: 'Balance fetching not yet implemented for this exchange' });
      return;
    }

    dispatchBalance({ type: 'LOADING', id: exchangeId });

    try {
      const endpoint = controller.endpoints.all
        ? controller.endpoints.all.replace(':userId', user.sub)
        : controller.endpoints.spot.replace(':userId', user.sub);
      const response = await fetch(`${API_BASE_URL}${endpoint}`);
      const data = await response.json();

      if (data.success) {
        dispatchBalance({ type: 'SUCCESS', id: exchangeId, data: data.data, notSupported: data.notSupported || [] });
      } else {
        dispatchBalance({ type: 'ERROR', id: exchangeId, error: data.error || 'Failed to fetch balances' });
      }
    } catch (error) {
      console.error(`Error fetching balances for ${exchange.exchangeName}:`, error);
      dispatchBalance({ type: 'ERROR', id: exchangeId, error: error.message || 'Failed to fetch balances' });
    }
  };

  if (!user) {
    return (
      <div className="page-content">
        <SEO
          title="Balances - Monitor balance on many crypto exchange accounts"
          description="View balance on your crypto exchange accounts on all integrated exchanges. Have your balance under control - multi-account transparency"
          ogImage="https://micropincher.com/images/og-image.jpg"
          ogUrl="https://micropincher.com/balances"
          twitterImage="https://micropincher.com/images/og-image.jpg"
          canonicalUrl="https://micropincher.com/balances"
        />
        <div style={{ color: '#8b949e', padding: '20px' }}>Please log in to view your balances.</div>
      </div>
    );
  }

  const futuresSettings = userExchangeSettings.filter(s => s.futures_enabled);
  const spotSettings    = userExchangeSettings.filter(s => s.spot_enabled);

  return (
    <div className="page-content">
      <SEO
        title="Balances - Monitor balance on many crypto exchange accounts"
        description="View balance on your crypto exchange accounts on all integrated exchanges. Have your balance under control - multi-account transparency"
        ogImage="https://micropincher.com/images/og-image.jpg"
        ogUrl="https://micropincher.com/balances"
        twitterImage="https://micropincher.com/images/og-image.jpg"
        canonicalUrl="https://micropincher.com/balances"
      />

      {/* ------------------------------------------------------------------ */}
      {/* Summary tables                                                       */}
      {/* ------------------------------------------------------------------ */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <CombinedSummaryTable
          futuresSettings={futuresSettings}
          spotSettings={spotSettings}
          allExchangeInfo={allExchangeInfo}
          exchangesWithKeys={exchangesWithKeys}
          balances={balances}
          loading={loading}
          errors={errors}
          notSupported={notSupported}
        />
        <TransferFeesTable userId={user.sub} exchangesWithKeys={exchangesWithKeys} />
      </div>

      {exchanges.length === 0 && userExchangeSettings.length === 0 && (
        <div style={{ color: '#8b949e', padding: '20px' }}>
          No exchanges configured. Please add API keys in Settings.
        </div>
      )}
    </div>
  );
};

export default Balances;
